import { useState, useEffect, useRef, useCallback } from 'react';
import { Package, Save, Plus, X, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import BrandSymbol from '@/components/BrandSymbol';
import ImageUploader from '@/components/admin/ImageUploader';

interface BundleProduct {
  product_id: string;
  variant_id: string | null;
  quantity: number;
  product_name?: string;
  price?: number;
  image_url?: string | null;
  variants?: { id: string; name: string; price: number }[];
}

interface BundleFormData {
  name: string;
  description: string;
  image_url: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  is_active: boolean;
  items: BundleProduct[];
  starts_at: string;
  ends_at: string;
  stock_limit: string;
}

interface SearchResult {
  id: string;
  name: string;
  base_price: number;
  primary_image_url: string | null;
  sku: string;
  status: string;
}

const INITIAL_FORM: BundleFormData = {
  name: '',
  description: '',
  image_url: '',
  discount_type: 'percentage',
  discount_value: 10,
  is_active: true,
  items: [],
  starts_at: '',
  ends_at: '',
  stock_limit: '',
};

export default function UpsellBundleEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [form, setForm] = useState<BundleFormData>(INITIAL_FORM);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Product search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isEditing) fetchBundle();
  }, [id]);

  // Close search results on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function fetchBundle() {
    try {
      setIsLoading(true);
      setError(null);
      const bundle = await api.get<any>(`/admin/bundles/${id}`);
      setForm({
        name: bundle.name,
        description: bundle.description || '',
        image_url: bundle.image_url || '',
        discount_type: bundle.discount_type,
        discount_value: Number(bundle.discount_value),
        is_active: bundle.is_active,
        items: (bundle.items || []).map((item: any) => ({
          product_id: item.product_id,
          variant_id: item.variant_id || null,
          quantity: item.quantity,
          product_name: item.product_name,
          price: Number(item.price),
          image_url: item.image_url,
        })),
        starts_at: bundle.starts_at ? bundle.starts_at.slice(0, 16) : '',
        ends_at: bundle.ends_at ? bundle.ends_at.slice(0, 16) : '',
        stock_limit: bundle.stock_limit != null ? String(bundle.stock_limit) : '',
      });
    } catch {
      setError('Failed to load bundle. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await api.get<{ products: SearchResult[] }>(`/admin/products?q=${encodeURIComponent(query)}&limit=8`);
        setSearchResults(data.products);
        setShowResults(true);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  async function handleAddProduct(product: SearchResult) {
    if (form.items.some(p => p.product_id === product.id)) {
      setError('This product is already in the bundle');
      return;
    }

    // Fetch variants for this product
    let variants: { id: string; name: string; price: number }[] = [];
    try {
      const data = await api.get<any>(`/admin/products/${product.id}`);
      if (data.product?.variants) {
        variants = data.product.variants
          .filter((v: any) => v.is_active)
          .map((v: any) => ({ id: v.id, name: v.name, price: Number(v.price) }));
      }
    } catch { /* use base price if variant fetch fails */ }

    setForm(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: product.id,
        variant_id: variants.length > 0 ? variants[0].id : null,
        quantity: 1,
        product_name: product.name,
        price: variants.length > 0 ? variants[0].price : product.base_price,
        image_url: product.primary_image_url,
        variants,
      }],
    }));

    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setError(null);
  }

  function handleRemoveProduct(productId: string) {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter(p => p.product_id !== productId),
    }));
  }

  function handleMoveItem(index: number, direction: 'up' | 'down') {
    const newItems = [...form.items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newItems.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    setForm(prev => ({ ...prev, items: newItems }));
  }

  function handleItemChange(index: number, field: string, value: any) {
    setForm(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      // Update price when variant changes
      if (field === 'variant_id' && newItems[index].variants) {
        const variant = newItems[index].variants!.find(v => v.id === value);
        if (variant) newItems[index].price = variant.price;
      }
      return { ...prev, items: newItems };
    });
  }

  function handleChange<K extends keyof BundleFormData>(field: K, value: BundleFormData[K]) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('Please enter a bundle name');
      return;
    }

    if (form.items.length === 0) {
      setError('Please add at least one product to the bundle');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const payload = {
        name: form.name,
        description: form.description,
        image_url: form.image_url || null,
        discount_type: form.discount_type,
        discount_value: form.discount_value,
        is_active: form.is_active,
        items: form.items.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
        })),
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        stock_limit: form.stock_limit ? parseInt(form.stock_limit) : null,
      };

      if (isEditing) {
        await api.put(`/admin/bundles/${id}`, payload);
      } else {
        await api.post('/admin/bundles', payload);
      }

      navigate('/admin/upsell/bundles');
    } catch {
      setError(`Failed to ${isEditing ? 'update' : 'create'} bundle. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  }

  // Calculate preview pricing
  const totalPrice = form.items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  let discountedPrice = totalPrice;
  if (form.discount_type === 'percentage') {
    discountedPrice = totalPrice * (1 - form.discount_value / 100);
  } else if (form.discount_type === 'fixed_amount') {
    discountedPrice = Math.max(0, totalPrice - form.discount_value);
  }
  const savings = totalPrice - discountedPrice;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading bundle...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Package className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Edit Bundle' : 'Create Bundle'}
          </h1>
        </div>
        <p className="text-gray-600">
          {isEditing ? 'Update bundle details and products' : 'Create a new product bundle with discounted pricing'}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main column */}
          <div className="flex-1 space-y-6">
            <Card title="Bundle Details" padding="lg">
              <div className="space-y-6">
                <Input
                  label="Bundle Name"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Starter Bundle"
                  required
                />

                <Textarea
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Everything you need to get started..."
                  rows={3}
                />

                <ImageUploader
                  label="Bundle Image"
                  value={form.image_url}
                  onChange={(url) => handleChange('image_url', url)}
                  targetWidth={1200}
                  targetHeight={675}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="discount_type" className="block text-sm font-medium text-stone-700 mb-1">
                      Discount Type
                    </label>
                    <select
                      id="discount_type"
                      value={form.discount_type}
                      onChange={(e) => handleChange('discount_type', e.target.value as 'percentage' | 'fixed_amount')}
                      className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed_amount">Fixed Amount</option>
                    </select>
                  </div>

                  <Input
                    label="Discount Value"
                    type="number"
                    value={String(form.discount_value)}
                    onChange={(e) => handleChange('discount_value', parseFloat(e.target.value) || 0)}
                    placeholder="10"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Active (visible to customers)
                  </label>
                </div>
              </div>
            </Card>

            <Card title="Scheduling & Stock" padding="lg">
              <div className="space-y-6">
                <p className="text-sm text-stone-500">
                  Optionally set a time window and/or stock limit for this bundle. Leave empty for no restrictions.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="starts_at" className="block text-sm font-medium text-stone-700 mb-1">
                      Available From
                    </label>
                    <input
                      id="starts_at"
                      type="datetime-local"
                      value={form.starts_at}
                      onChange={(e) => handleChange('starts_at', e.target.value)}
                      className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="ends_at" className="block text-sm font-medium text-stone-700 mb-1">
                      Available Until
                    </label>
                    <input
                      id="ends_at"
                      type="datetime-local"
                      value={form.ends_at}
                      onChange={(e) => handleChange('ends_at', e.target.value)}
                      className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <Input
                  label="Stock Limit"
                  type="number"
                  value={form.stock_limit}
                  onChange={(e) => handleChange('stock_limit', e.target.value)}
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>
            </Card>

            <Card title="Bundle Products" padding="lg">
              <div className="space-y-4">
                {/* Product search */}
                <div ref={searchRef} className="relative">
                  <label className="block text-sm font-medium text-stone-700 mb-1">Add Product</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" aria-hidden="true" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onFocus={() => searchResults.length > 0 && setShowResults(true)}
                      placeholder="Search products by name or SKU..."
                      className="w-full rounded-lg border border-stone-300 pl-10 pr-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      aria-label="Search products"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="h-4 w-4 border-2 border-stone-300 border-t-green-600 rounded-full animate-spin" />
                      </div>
                    )}
                  </div>

                  {/* Search results dropdown */}
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {searchResults.map(product => {
                        const alreadyAdded = form.items.some(p => p.product_id === product.id);
                        return (
                          <button
                            key={product.id}
                            type="button"
                            disabled={alreadyAdded}
                            onClick={() => handleAddProduct(product)}
                            className={`w-full flex items-center gap-3 p-3 text-left hover:bg-stone-50 border-b border-stone-100 last:border-0 ${alreadyAdded ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div className="w-10 h-10 rounded bg-stone-100 shrink-0 overflow-hidden">
                              {product.primary_image_url ? (
                                <img src={product.primary_image_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <BrandSymbol className="h-4 w-4 text-stone-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-stone-900 truncate">{product.name}</p>
                              <p className="text-xs text-stone-500">{product.sku} &middot; {formatCurrency(product.base_price)}</p>
                            </div>
                            {alreadyAdded ? (
                              <span className="text-xs text-stone-400">Added</span>
                            ) : (
                              <Plus className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg p-4 text-center text-sm text-stone-500">
                      No products found
                    </div>
                  )}
                </div>

                {/* Items list */}
                {form.items.length === 0 ? (
                  <p className="text-stone-500 text-sm italic py-4">No products added yet. Search and add at least one product.</p>
                ) : (
                  <div className="space-y-2">
                    {form.items.map((item, index) => (
                      <div key={item.product_id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleMoveItem(index, 'up')}
                            disabled={index === 0}
                            className="p-0.5 text-stone-400 hover:text-stone-600 disabled:opacity-30"
                            aria-label="Move up"
                          >
                            <ChevronUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveItem(index, 'down')}
                            disabled={index === form.items.length - 1}
                            className="p-0.5 text-stone-400 hover:text-stone-600 disabled:opacity-30"
                            aria-label="Move down"
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {/* Product image */}
                        <div className="w-12 h-12 rounded bg-stone-200 shrink-0 overflow-hidden">
                          {item.image_url ? (
                            <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BrandSymbol className="h-5 w-5 text-stone-300" />
                            </div>
                          )}
                        </div>

                        {/* Product info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">{item.product_name || 'Unknown Product'}</p>
                          <p className="text-xs text-green-700 font-medium">{formatCurrency(item.price || 0)}</p>
                        </div>

                        {/* Variant selector */}
                        {item.variants && item.variants.length > 1 && (
                          <select
                            value={item.variant_id || ''}
                            onChange={(e) => handleItemChange(index, 'variant_id', e.target.value || null)}
                            className="text-xs rounded border border-stone-300 px-2 py-1 max-w-[120px]"
                            aria-label={`Variant for ${item.product_name}`}
                          >
                            {item.variants.map(v => (
                              <option key={v.id} value={v.id}>{v.name} ({formatCurrency(v.price)})</option>
                            ))}
                          </select>
                        )}

                        {/* Quantity */}
                        <div className="flex items-center gap-1.5">
                          <label className="text-xs text-stone-500 sr-only">Qty</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                            min="1"
                            className="w-14 text-center text-sm rounded border border-stone-300 px-1 py-1"
                            aria-label={`Quantity for ${item.product_name}`}
                          />
                        </div>

                        {/* Remove */}
                        <button
                          type="button"
                          onClick={() => handleRemoveProduct(item.product_id)}
                          className="p-1.5 text-red-400 hover:text-red-600 shrink-0"
                          aria-label={`Remove ${item.product_name}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar — Preview & Actions */}
          <div className="lg:w-72 space-y-6">
            <Card title="Preview" padding="lg">
              <div className="space-y-3">
                {form.items.length === 0 ? (
                  <p className="text-sm text-stone-400 italic">Add products to see pricing preview</p>
                ) : (
                  <>
                    <div className="text-sm space-y-1">
                      {form.items.map(item => (
                        <div key={item.product_id} className="flex justify-between">
                          <span className="text-stone-600 truncate mr-2">{item.product_name} x{item.quantity}</span>
                          <span className="text-stone-900 shrink-0">{formatCurrency((item.price || 0) * item.quantity)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-stone-200 pt-2 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">Subtotal</span>
                        <span className="text-stone-900">{formatCurrency(totalPrice)}</span>
                      </div>
                      {savings > 0 && (
                        <div className="flex justify-between text-sm text-green-700">
                          <span>Discount ({form.discount_type === 'percentage' ? `${form.discount_value}%` : formatCurrency(form.discount_value)})</span>
                          <span>-{formatCurrency(savings)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-base font-bold">
                        <span>Bundle Price</span>
                        <span className="text-green-700">{formatCurrency(discountedPrice)}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Card>

            <div className="space-y-3">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                isLoading={isSaving}
              >
                <Save className="w-5 h-5 mr-2" aria-hidden="true" />
                {isEditing ? 'Update Bundle' : 'Create Bundle'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="lg"
                className="w-full"
                onClick={() => navigate('/admin/upsell/bundles')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
